import {getLocationQueryObject} from "./utils/text";
import clientStorage from "./utils/client-storage";
import {detect} from "detect-browser";
import {generateHash} from "./utils/text";
import {isProfitUser, isCryptoUser, isWebviewCountryOf} from "./utils/config.utils";
import {supportedLanguagePaths} from "./routes";
import {webviewContext} from "@vicman-wi/webview-api/context";
import {webviewGetWebApiInfo, webviewGetWebApiInfoIsSupport} from "@vicman-wi/webview-api/functions/app/get-web-api-info";
import {webviewGetCommonParams} from "@vicman-wi/webview-api/functions/app/get-common-params";

export default async function() {
  let query = getLocationQueryObject();
  const platform = detect();
  const isMobile = ["android os", "ios"].indexOf(platform.os.toLowerCase()) > -1;
  const isWebviewAndroid = typeof query["aid"] === "string" || window["VicmanNativeApi"];
  const isWebviewIos = typeof query["vicman_unified_id"] === "string"
    || window.webkit?.["messageHandlers"]?.["VicmanNativeApi"];
  const isWebview = isWebviewAndroid || isWebviewIos;

  const _webviewContext = webviewContext({
    platform: isWebviewAndroid ? "android" : (isWebviewIos ? "ios" : "unrecognized"),
    appId: parseInt(query["app_id"]) || 0,
    versionCode: parseInt(query["version_code"]) || 0,
  });

  if (isWebview) {
    if (typeof query["aid"] === "undefined" && typeof query["vicman_unified_id"] === "undefined") {
      const params = await getWebviewCommonParams(_webviewContext);
      Object.assign(query, params);
    }

    webviewContext({
      appId: parseInt(query["app_id"]) || 0,
      versionCode: parseInt(query["version_code"]) || 0,
    });

    const info = await getWebviewApiInfo(_webviewContext);
    if (info) {
      webviewContext({webApiVersion: info.web_api_version});
    }
  }

  let clientToken;
  if (isWebview) {
    clientToken = typeof query["aid"] === "string"
      ? `w:a:${query["aid"]}`
      : `w:i:${query["vicman_unified_id"]}`;
  } else {
    const _clientToken = clientStorage.getClientToken();
    if (_clientToken) {
      clientToken = _clientToken;
    } else {
      clientToken = `b:${isMobile ? "m" : "d"}:${generateHash()}`;
      clientStorage.setClientToken(clientToken);
    }
  }

  if (!clientStorage.hasFirstVisitAt()) {
    clientStorage.setFirstVisitAt(Date.now());
  }

  const firstUrlPath = (window.location.pathname.split("/")[1] || "").toLowerCase();
  if (supportedLanguagePaths.includes(firstUrlPath)) {
    clientStorage.setSelectedLang(firstUrlPath);
  }

  const locale = query["locale"]
    || query["lang"]
    || clientStorage.getSelectedLang()
    || window.navigator.language
    || "en";

  window.clientConfig = {
    token: clientToken,
    locale: locale,
    lang: locale.substring(0, 2).toLowerCase(),
    queryParams: query,
    platform: platform,
    isTester: !/^[wb]:[aimd]:[a-f0-9-]+$/i.test(clientToken),
    isWeb: !isWebview,
    isWebMobile: !isWebview && isMobile,
    isWebMobileAndroid: platform.os.toLowerCase() === "android os",
    isWebMobileIos: platform.os.toLowerCase() === "ios",
    isWebDesktop: !isWebview && !isMobile,
    isWebview: isWebview,
    isWebviewAndroid: isWebviewAndroid,
    isWebviewIos: isWebviewIos,
    isAnyMobile: isWebview || isMobile,
    isAnyIOS: platform.os.toLowerCase() === "ios",
    isAnyAndroid: platform.os.toLowerCase().indexOf("android") > -1,
    isPro: query["is_pro"] === "1" || query["is_pro"] === true,
    isTrial: query["subs_is_trial"] === "1" || query["subs_is_trial"] === true,
    isCancelledTrial: (query["subs_is_trial"] === "1" || query["subs_is_trial"] === true) && query["subs_state"] === "cancelled",
    isCancelled: query["subs_state"] === "cancelled",
    isInfluencer: query["infl"] === "1",
    subscriptionSku: query["subs_id"],
    splitGroupId: undefined,
    isNew: Date.now() < (clientStorage.getFirstVisitAt() + 43_200_000), // 12h
    geoipCountryCode: null,
    appTabs: decodeURIComponent(query["app_tabs"] || "").split(","),
    isHiddenTab: query["showed"] !== "tab",
    isCryptoUser: isCryptoUser(query),
    geolocation: {},
    isWebMobileDesktopMode: false,
  }

  window.clientConfig.isProfitUser = isProfitUser(query);
  window.clientConfig.isCheapUser = !window.clientConfig.isProfitUser;

  window.clientConfig.isBrazilianUser = isWebviewCountryOf("br");
  window.clientConfig.isTurkeyUser = isWebviewCountryOf("tr");

  window.clientConfig.isWebMobileDesktopMode = (function(){
    const webkitVer = parseInt(/WebKit\/(\d+)|$/.exec(window.navigator.appVersion || "")[1], 10); // also matches AppleWebKit
    const isGoogle = webkitVer && window.navigator.vendor.indexOf("Google") === 0;  // Also true for Opera Mobile and maybe others
    const isAndroid = isGoogle && window.navigator.userAgent.indexOf("Android") > 0;  // Careful - Firefox and Windows Mobile also have Android in user agent
    return !isAndroid
      && isGoogle
      && ((window.navigator.platform || "").indexOf('Linux a') === 0)
      && "ontouchstart" in window.document.documentElement;
  })();

  if (isWebview) {
    const aid = query["aid"] || query["vicman_unified_id"];
    const num = parseInt(aid.substring(aid.length - 2), 16);
    const group = Math.floor(num / (255 / 10)) + 1;

    window.clientConfig.splitGroupId = Math.min(group, 10); // fix for ff => 11 group
  } else {
    const groupId = query["split_group"] || clientStorage.getSplitGroupId();
    if (groupId) {
      window.clientConfig.splitGroupId = parseInt(groupId);
    } else {
      window.clientConfig.splitGroupId = Math.floor(Math.random() * 10) + 1;
    }

    clientStorage.setSplitGroupId(window.clientConfig.splitGroupId);
  }
}

async function getWebviewCommonParams(context) {
  const result = {};

  try {
    const data = await Promise.race([
      webviewGetCommonParams(context),
      new Promise((_, reject) => setTimeout(() => reject("timeout"), 100)),
    ]);

    Object.assign(result, data);
  } catch (err) {
    console.warn("Webview getCommonParams took over 100ms");
  }

  return result;
}

async function getWebviewApiInfo(context) {
  if (webviewGetWebApiInfoIsSupport(context)) {
    try {
      return await Promise.race([
        webviewGetWebApiInfo(context),
        new Promise((_, reject) => setTimeout(() => reject("timeout"), 100)),
      ]);
    } catch (err) {
      console.warn("Webview getWebApiInfo took over 100ms");
    }
  }

  return null;
}
